import { useEffect, useRef, useCallback, useState } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { AddressSearch } from './AddressSearch'
import type { AddressSuggestion } from '../../api/addresses'
import styles from './AreaDrawer.module.css'

interface AreaDrawerProps {
  mode: 'polygon' | 'radius'
  // For radius mode
  center?: { lat: number; lng: number }
  radiusKm?: number
  onCenterChange?: (center: { lat: number; lng: number }) => void
  onRadiusChange?: (radiusKm: number) => void
  // For polygon mode
  boundaryWkt?: string
  onBoundaryChange?: (wkt: string) => void
  // Initial map view
  initialCenter?: [number, number]
  initialZoom?: number
}

const DARK_MAP_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    'carto-dark': {
      type: 'raster',
      tiles: [
        'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png',
        'https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png',
        'https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png',
      ],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors, © CARTO',
    },
  },
  layers: [
    {
      id: 'carto-dark',
      type: 'raster',
      source: 'carto-dark',
      minzoom: 0,
      maxzoom: 20,
    },
  ],
}

// Generate circle polygon from center and radius
function generateCirclePolygon(center: { lat: number; lng: number }, radiusKm: number, points = 64): GeoJSON.Feature<GeoJSON.Polygon> {
  const coords: [number, number][] = []
  for (let i = 0; i <= points; i++) {
    const angle = (i / points) * 2 * Math.PI
    // Approximate conversion: 1 degree latitude ≈ 111km
    const latOffset = (radiusKm / 111) * Math.sin(angle)
    const lngOffset = (radiusKm / (111 * Math.cos(center.lat * Math.PI / 180))) * Math.cos(angle)
    coords.push([center.lng + lngOffset, center.lat + latOffset])
  }
  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [coords],
    },
  }
}

// Parse WKT polygon to GeoJSON
function wktToGeoJSON(wkt: string): GeoJSON.Feature<GeoJSON.Polygon> | null {
  const match = wkt.match(/POLYGON\s*\(\(([^)]+)\)\)/i)
  if (!match) return null
  
  const coordsStr = match[1]
  const coords = coordsStr.split(',').map(pair => {
    const [lng, lat] = pair.trim().split(/\s+/).map(Number)
    return [lng, lat] as [number, number]
  })
  
  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [coords],
    },
  }
}

// Convert GeoJSON polygon to WKT
function geoJSONToWkt(feature: GeoJSON.Feature<GeoJSON.Polygon>): string {
  const coords = feature.geometry.coordinates[0]
  const pairs = coords.map(([lng, lat]) => `${lng} ${lat}`).join(', ')
  return `POLYGON((${pairs}))`
}

export function AreaDrawer({
  mode,
  center,
  radiusKm = 3,
  onCenterChange,
  boundaryWkt,
  onBoundaryChange,
  initialCenter = [112.95, -2.55], // Sampit default
  initialZoom = 12,
}: AreaDrawerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const markerRef = useRef<maplibregl.Marker | null>(null)
  const markersRef = useRef<maplibregl.Marker[]>([])
  const [polygonPoints, setPolygonPoints] = useState<[number, number][]>([])
  const [isDrawing, setIsDrawing] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const isDrawingRef = useRef(false)
  const polygonPointsRef = useRef<[number, number][]>([])
  const onBoundaryChangeRef = useRef(onBoundaryChange)

  // Handle address selection - fly to location
  const handleAddressSelect = useCallback((suggestion: AddressSuggestion) => {
    if (!mapRef.current) return
    
    const { lat, lng } = suggestion.coordinates
    mapRef.current.flyTo({
      center: [lng, lat],
      zoom: 14,
      duration: 1000,
    })
    setSearchQuery('')
  }, [])
  
  // Keep refs in sync with state
  useEffect(() => {
    isDrawingRef.current = isDrawing
  }, [isDrawing])
  
  useEffect(() => {
    polygonPointsRef.current = polygonPoints
  }, [polygonPoints])
  
  useEffect(() => {
    onBoundaryChangeRef.current = onBoundaryChange
  }, [onBoundaryChange])

  // Update circle layer
  const updateCircle = useCallback(() => {
    if (!mapRef.current || mode !== 'radius' || !center) return
    
    const circleFeature = generateCirclePolygon(center, radiusKm)
    const source = mapRef.current.getSource('area') as maplibregl.GeoJSONSource
    if (source) {
      source.setData(circleFeature)
    }
  }, [mode, center, radiusKm])

  // Update polygon layer and markers
  const updatePolygonDisplay = useCallback((points: [number, number][], updateWkt = true) => {
    if (!mapRef.current || mode !== 'polygon') return
    
    const map = mapRef.current
    const source = map.getSource('area') as maplibregl.GeoJSONSource
    if (!source) return

    // Clear existing point markers
    markersRef.current.forEach(m => m.remove())
    markersRef.current = []

    if (points.length === 0) {
      source.setData({ type: 'FeatureCollection', features: [] })
      if (updateWkt) onBoundaryChangeRef.current?.('')
      return
    }

    // Create draggable markers for each point
    points.forEach((coord, index) => {
      const el = document.createElement('div')
      el.className = styles.pointMarker
      el.innerHTML = `<span>${index + 1}</span>`
      
      const marker = new maplibregl.Marker({ element: el, draggable: true })
        .setLngLat(coord)
        .addTo(map)
      
      marker.on('drag', () => {
        const lngLat = marker.getLngLat()
        const newPoints = [...polygonPointsRef.current]
        newPoints[index] = [lngLat.lng, lngLat.lat]
        
        // Update polygon shape while dragging
        if (newPoints.length >= 3) {
          const closedPoints = [...newPoints, newPoints[0]]
          const feature: GeoJSON.Feature<GeoJSON.Polygon> = {
            type: 'Feature',
            properties: {},
            geometry: { type: 'Polygon', coordinates: [closedPoints] },
          }
          source.setData(feature)
        }
      })
      
      marker.on('dragend', () => {
        const lngLat = marker.getLngLat()
        setPolygonPoints(prev => {
          const newPoints = [...prev]
          newPoints[index] = [lngLat.lng, lngLat.lat]
          
          // Update WKT
          if (newPoints.length >= 3) {
            const closedPoints = [...newPoints, newPoints[0]]
            const feature: GeoJSON.Feature<GeoJSON.Polygon> = {
              type: 'Feature',
              properties: {},
              geometry: { type: 'Polygon', coordinates: [closedPoints] },
            }
            onBoundaryChangeRef.current?.(geoJSONToWkt(feature))
          }
          
          return newPoints
        })
      })
      
      markersRef.current.push(marker)
    })

    // Draw polygon if we have enough points
    if (points.length >= 3) {
      const closedPoints = [...points, points[0]]
      const feature: GeoJSON.Feature<GeoJSON.Polygon> = {
        type: 'Feature',
        properties: {},
        geometry: { type: 'Polygon', coordinates: [closedPoints] },
      }
      source.setData(feature)
      
      if (updateWkt) {
        onBoundaryChangeRef.current?.(geoJSONToWkt(feature))
      }
    } else {
      source.setData({ type: 'FeatureCollection', features: [] })
    }
  }, [mode])

  // Initialize map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: DARK_MAP_STYLE,
      center: center ? [center.lng, center.lat] : initialCenter,
      zoom: initialZoom,
    })

    map.addControl(new maplibregl.NavigationControl(), 'top-right')

    map.on('load', () => {
      // Add area source and layer
      map.addSource('area', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })

      map.addLayer({
        id: 'area-fill',
        type: 'fill',
        source: 'area',
        paint: {
          'fill-color': '#FFC73C',
          'fill-opacity': 0.2,
        },
      })

      map.addLayer({
        id: 'area-outline',
        type: 'line',
        source: 'area',
        paint: {
          'line-color': '#FFC73C',
          'line-width': 2,
        },
      })

      // Initialize with existing data
      if (mode === 'radius' && center) {
        updateCircle()
      } else if (mode === 'polygon' && boundaryWkt) {
        const feature = wktToGeoJSON(boundaryWkt)
        if (feature) {
          // Extract points for editing (remove closing point)
          const coords = feature.geometry.coordinates[0].slice(0, -1) as [number, number][]
          setPolygonPoints(coords)
          polygonPointsRef.current = coords
          // Use setTimeout to ensure map is ready
          setTimeout(() => updatePolygonDisplay(coords, false), 100)
        }
      }
    })

    // Handle click for placing center (radius mode) or adding points (polygon mode)
    map.on('click', (e) => {
      if (mode === 'radius') {
        const newCenter = { lat: e.lngLat.lat, lng: e.lngLat.lng }
        onCenterChange?.(newCenter)
        
        // Update marker
        if (markerRef.current) {
          markerRef.current.setLngLat([newCenter.lng, newCenter.lat])
        } else {
          const el = document.createElement('div')
          el.className = styles.centerMarker
          markerRef.current = new maplibregl.Marker({ element: el, draggable: true })
            .setLngLat([newCenter.lng, newCenter.lat])
            .addTo(map)
          
          markerRef.current.on('dragend', () => {
            const lngLat = markerRef.current!.getLngLat()
            onCenterChange?.({ lat: lngLat.lat, lng: lngLat.lng })
          })
        }
      } else if (mode === 'polygon' && isDrawingRef.current) {
        // Add new point when drawing
        const newPoint: [number, number] = [e.lngLat.lng, e.lngLat.lat]
        const updated = [...polygonPointsRef.current, newPoint]
        setPolygonPoints(updated)
        polygonPointsRef.current = updated
        updatePolygonDisplay(updated)
      }
    })

    mapRef.current = map

    return () => {
      markersRef.current.forEach(m => m.remove())
      markersRef.current = []
      if (markerRef.current) {
        markerRef.current.remove()
        markerRef.current = null
      }
      map.remove()
      mapRef.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Update circle when center or radius changes
  useEffect(() => {
    if (mode === 'radius' && center && mapRef.current?.loaded()) {
      updateCircle()
      
      // Update or create marker
      if (markerRef.current) {
        markerRef.current.setLngLat([center.lng, center.lat])
      } else if (mapRef.current) {
        const el = document.createElement('div')
        el.className = styles.centerMarker
        markerRef.current = new maplibregl.Marker({ element: el, draggable: true })
          .setLngLat([center.lng, center.lat])
          .addTo(mapRef.current)
        
        markerRef.current.on('dragend', () => {
          const lngLat = markerRef.current!.getLngLat()
          onCenterChange?.({ lat: lngLat.lat, lng: lngLat.lng })
        })
      }
    }
  }, [mode, center, radiusKm, updateCircle, onCenterChange])

  const handleClearPolygon = useCallback(() => {
    setPolygonPoints([])
    polygonPointsRef.current = []
    updatePolygonDisplay([])
  }, [updatePolygonDisplay])

  const handleUndoPoint = useCallback(() => {
    const updated = polygonPointsRef.current.slice(0, -1)
    setPolygonPoints(updated)
    polygonPointsRef.current = updated
    updatePolygonDisplay(updated)
  }, [updatePolygonDisplay])

  return (
    <div className={styles.container}>
      <div ref={containerRef} className={styles.map} />
      
      {/* Search bar overlay */}
      <div className={styles.searchOverlay}>
        <AddressSearch
          value={searchQuery}
          onChange={setSearchQuery}
          onSelect={handleAddressSelect}
          placeholder="Search location to navigate..."
        />
      </div>
      
      <div className={styles.controls}>
        {mode === 'radius' && (
          <div className={styles.hint}>
            <i className="ri-cursor-line" /> Click on the map to set center point
          </div>
        )}
        
        {mode === 'polygon' && (
          <>
            <div className={styles.drawControls}>
              <button
                type="button"
                className={`${styles.drawButton} ${isDrawing ? styles.drawButtonActive : ''}`}
                onClick={() => setIsDrawing(!isDrawing)}
              >
                <i className={isDrawing ? 'ri-stop-line' : 'ri-pencil-line'} />
                {isDrawing ? 'STOP DRAWING' : 'START DRAWING'}
              </button>
              <button
                type="button"
                className={styles.iconButton}
                onClick={handleUndoPoint}
                disabled={polygonPoints.length === 0}
                title="Undo last point"
              >
                <i className="ri-arrow-go-back-line" />
              </button>
              <button
                type="button"
                className={styles.iconButton}
                onClick={handleClearPolygon}
                disabled={polygonPoints.length === 0}
                title="Clear polygon"
              >
                <i className="ri-delete-bin-line" />
              </button>
            </div>
            <div className={styles.hint}>
              {isDrawing ? (
                <>
                  <i className="ri-cursor-line" /> Click to add points ({polygonPoints.length} points)
                </>
              ) : polygonPoints.length > 0 ? (
                <>
                  <i className="ri-drag-move-line" /> Drag points to edit • Click "Start Drawing" to add more
                </>
              ) : (
                <>
                  <i className="ri-information-line" /> Click "Start Drawing" to draw polygon
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
